import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

interface Word {
  id: string;
  word: string;
  results: {
    definition?: string;
    synonyms?: string[];
  }[];
}

interface WordState {
  words: Word[];
  originalWords: Word[];
  searchResults: Word[];
  favoriteWords: Word[];
  loading: boolean;
  error: string | null;
  isFavAnimating: boolean;
}

const API_HOST = "wordsapiv1.p.rapidapi.com";
const API_KEY = "2e0e88522dmsh31d89af60f830d6p1c0eb6jsne3220dedfb3a";

export const fetchWords = createAsyncThunk<
  Word[],
  void,
  { rejectValue: string }
>("words/fetchWords", async (_, { rejectWithValue }) => {
  try {
    const promises = Array.from({ length: 6 }, async () => {
      const response = await axios.get(
        `https://wordsapiv1.p.rapidapi.com/words/?random=true`,
        {
          headers: {
            "x-rapidapi-host": API_HOST,
            "x-rapidapi-key": API_KEY,
          },
        }
      );
      return response.data;
    });
    return await Promise.all(promises);
  } catch (error) {
    console.error("Error fetching words:", error);
    return rejectWithValue("Failed to fetch words.");
  }
});

const sortWordsBy = (words: Word[], sortBy: string): Word[] => {
  switch (sortBy) {
    case "alphabetical":
      return [...words].sort((a, b) => a.word.localeCompare(b.word));
    case "ascending":
      return [...words].sort((a, b) => a.word.length - b.word.length);
    default:
      return words;
  }
};

const initialState: WordState = {
  words: [],
  originalWords: [],
  searchResults: [],
  favoriteWords: [],
  loading: false,
  error: null,
  isFavAnimating: false,
};

const wordSlice = createSlice({
  name: "words",
  initialState,
  reducers: {
    sortWords: (
      state,
      action: PayloadAction<{ sortBy: string; isFavorite: boolean }>
    ) => {
      if (action.payload.isFavorite) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          const favoriteWords = userData.favorites || [];
          const sortedFavorites = sortWordsBy(
            favoriteWords,
            action.payload.sortBy
          );
          state.favoriteWords = sortedFavorites;
          userData.favorites = sortedFavorites;
          localStorage.setItem("user", JSON.stringify(userData));
          window.location.reload();
        }
      } else {
        state.words = sortWordsBy(state.originalWords, action.payload.sortBy);
      }
    },

    searchWords: (
      state,
      action: PayloadAction<{ searchTerm: string; isFavorite: boolean }>
    ) => {
      const searchTerm = action.payload.searchTerm.toLowerCase();

      if (action.payload.isFavorite) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          const favoriteWords = userData.favorites || [];
          state.searchResults = favoriteWords.filter((word: any) =>
            word.word.toLowerCase().includes(searchTerm)
          );
          state.favoriteWords =
            state.searchResults.length > 0 ? state.searchResults : [];
        }
      } else {
        state.searchResults = state.originalWords.filter((word) =>
          word.word.toLowerCase().includes(searchTerm)
        );
        state.words = state.searchResults.length > 0 ? state.searchResults : [];
      }
    },

    syncFavoriteWords: (state, action: PayloadAction<Word[]>) => {
      state.favoriteWords = action.payload;
    },

    resetSearch: (state, action: PayloadAction<{ isFavorite: boolean }>) => {
      state.searchResults = [];

      if (action.payload.isFavorite) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          state.searchResults = userData.favorites || [];
        }
      } else {
        state.words = [...state.originalWords];
      }
    },

    setFilteredWords: (state, action: PayloadAction<Word[]>) => {
      state.words = action.payload;
    },

    triggerFavAnimation: (state) => {
      state.isFavAnimating = true;
    },

    resetFavAnimation: (state) => {
      state.isFavAnimating = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchWords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWords.fulfilled, (state, action: PayloadAction<Word[]>) => {
        state.loading = false;
        state.words = action.payload;
        state.originalWords = [...action.payload];
      })
      .addCase(
        fetchWords.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred.";
        }
      );
  },
});

export const {
  syncFavoriteWords,
  sortWords,
  searchWords,
  resetSearch,
  setFilteredWords,
  triggerFavAnimation,
  resetFavAnimation,
} = wordSlice.actions;

export default wordSlice.reducer;
